import { Dropdown } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { usePinCompaniesMutation } from '@/api/workspace/index.ts';
import { selectPinnedCompanies } from '@/api/workspace/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { moveElement } from '@/utils/array.ts';

type Props = {
  companyId: number;
  preventReorderingPinned: boolean;
};

export function ClientMenuButton({ companyId, preventReorderingPinned }: Props) {
  const pinnedCompanies = useAppSelector(selectPinnedCompanies);
  const [pinCompanies] = usePinCompaniesMutation();

  const isPinned = pinnedCompanies.includes(companyId);

  const pinCompany = () => {
    pinCompanies({ pinnedCompanies: [...pinnedCompanies, companyId] });
  };

  const unpinCompany = () => {
    pinCompanies({ pinnedCompanies: pinnedCompanies.filter((id) => id !== companyId) });
  };

  const moveUpPinCompany = () => {
    const index = pinnedCompanies.indexOf(companyId);
    const newPinnedCompanies = moveElement(pinnedCompanies, index, index - 1);
    pinCompanies({ pinnedCompanies: newPinnedCompanies });
  };

  const moveDownPinCompany = () => {
    const index = pinnedCompanies.indexOf(companyId);
    const newPinnedCompanies = moveElement(pinnedCompanies, index, index + 1);
    pinCompanies({ pinnedCompanies: newPinnedCompanies });
  };

  return (
    <td>
      <Dropdown drop="end">
        <Dropdown.Toggle variant="flat-secondary" className="btn-sm btn-icon">
          <i className="icon">more_vert</i>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {isPinned ? (
            <PinnedClientMenu
              companyId={companyId}
              unpinCompany={unpinCompany}
              moveUpPinCompany={moveUpPinCompany}
              moveDownPinCompany={moveDownPinCompany}
              preventReorderingPinned={preventReorderingPinned}
            />
          ) : (
            <UnpinnedClientMenu pinCompany={pinCompany} />
          )}
        </Dropdown.Menu>
      </Dropdown>
    </td>
  );
}

function PinnedClientMenu({
  companyId,
  preventReorderingPinned,
  unpinCompany,
  moveDownPinCompany,
  moveUpPinCompany,
}: {
  companyId: number;
  preventReorderingPinned: boolean;
  unpinCompany: () => void;
  moveDownPinCompany: () => void;
  moveUpPinCompany: () => void;
}) {
  const pinnedCompanies = useAppSelector(selectPinnedCompanies);
  const pinIndex = pinnedCompanies.indexOf(companyId);

  const canMoveUp = pinIndex > 0;
  const canMoveDown = pinIndex < pinnedCompanies.length - 1;

  return (
    <>
      {canMoveUp && (
        <Dropdown.Item onClick={moveUpPinCompany} disabled={preventReorderingPinned}>
          <FormattedMessage id="clientSelection.table.action.moveUp" />
        </Dropdown.Item>
      )}
      {canMoveDown && (
        <Dropdown.Item onClick={moveDownPinCompany} disabled={preventReorderingPinned}>
          <FormattedMessage id="clientSelection.table.action.moveDown" />
        </Dropdown.Item>
      )}
      <Dropdown.Item onClick={unpinCompany}>
        <FormattedMessage id="clientSelection.table.action.unpin" />
      </Dropdown.Item>
    </>
  );
}

function UnpinnedClientMenu({ pinCompany }: { pinCompany: () => void }) {
  return (
    <Dropdown.Item onClick={pinCompany}>
      <FormattedMessage id="clientSelection.table.action.pin" />
    </Dropdown.Item>
  );
}
